/**
 * Lightweight analytics tracker for public pages.
 * Tracks page views, scroll depth, time on page, and exit events.
 *
 * Server counterpart: app/Http/Controllers/Api/AnalyticsEventController.php.
 * See initAnalytics() for the time-on-page delta convention both sides share.
 */

import { apiFetch } from '@/lib/api-fetch';
import { CMS_PATH_PREFIXES } from '@/lib/cms-paths.generated';

const API_URL = '/api/analytics/event';

function getVisitorId(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)_lrt_vid=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
}

function captureClickIds(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    const clickIds: Record<string, string> = {};
    const keys = ['gclid', 'fbclid', 'ttclid', 'li_fat_id'];
    for (const key of keys) {
        const value = params.get(key);
        if (value) {
            clickIds[key] = value;
        }
    }
    if (Object.keys(clickIds).length > 0) {
        sessionStorage.setItem('_lrt_click_ids', JSON.stringify(clickIds));
    }
    return Object.keys(clickIds).length > 0 ? clickIds : JSON.parse(sessionStorage.getItem('_lrt_click_ids') || '{}');
}

function getSessionId(): string {
    let id = sessionStorage.getItem('_lrt_sid');
    if (!id) {
        id = crypto.randomUUID().replace(/-/g, '').slice(0, 64);
        sessionStorage.setItem('_lrt_sid', id);
    }
    return id;
}

function getUTMParams(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
        const val = params.get(key);
        if (val) utm[key] = val;
    }
    return utm;
}

function getArticleId(): number | null {
    const meta = document.querySelector('meta[name="article-id"]');
    return meta ? parseInt(meta.getAttribute('content') || '', 10) || null : null;
}

/**
 * Reads the Inertia v3 page props from the in-DOM JSON island. Inertia v3
 * stopped exposing `window.__page` (v2 behaviour); the page state now lives
 * exclusively in `<script data-page type="application/json">` until the React
 * tree hydrates. The tracker fires from a top-level `useEffect`, so the script
 * tag is reliably present by the time this runs.
 */
function getInertiaPageProps(): Record<string, unknown> | null {
    try {
        const script = document.querySelector('script[data-page]');
        if (!script || !script.textContent) return null;
        const parsed = JSON.parse(script.textContent) as { props?: Record<string, unknown> };
        return parsed.props ?? null;
    } catch {
        return null;
    }
}

function getExperimentAssignments(): Record<string, unknown> | null {
    const props = getInertiaPageProps();
    const experiments = props?.experiments;
    if (experiments && typeof experiments === 'object' && Object.keys(experiments).length > 0) {
        return experiments as Record<string, unknown>;
    }
    return null;
}

/**
 * The page a tracker instance was mounted for. Captured once at init and sent
 * with every event of that page life. `window.location` must NOT be read at
 * send time: an Inertia navigation rewrites the URL before the old layout
 * unmounts, so a flush from the effect cleanup would otherwise attribute the
 * old page's time to the new path.
 */
interface PageContext {
    page_path: string;
    page_title: string;
    article_id: number | null;
}

function send(data: Record<string, unknown>, ctx?: PageContext): void {
    const experiments = getExperimentAssignments();
    if (experiments) {
        data.experiments = experiments;
    }

    const body = JSON.stringify({
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        click_ids: captureClickIds(),
        page_path: ctx?.page_path ?? window.location.pathname,
        page_title: ctx?.page_title ?? document.title,
        article_id: ctx?.article_id ?? getArticleId(),
        referrer: document.referrer || null,
        ...getUTMParams(),
        ...data,
    });

    try {
        if (navigator.sendBeacon) {
            navigator.sendBeacon(API_URL, new Blob([body], { type: 'application/json' }));
        } else {
            fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
        }
    } catch {
        // Silent
    }
}

/** True for any CMS/admin URL — matched on the first path segment, never a bare prefix. */
export function isCmsPath(pathname: string): boolean {
    return CMS_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

const noop = (): void => {};

/** The teardown of the instance currently mounted, so a re-init never stacks listeners. */
let activeTeardown: (() => void) | null = null;

/**
 * Start tracking the current page. Returns a teardown; call it from the effect
 * cleanup (PublicLayout is non-persistent, so every Inertia navigation
 * unmounts and re-mounts it). Idempotent: initialising again before the
 * previous teardown ran tears the previous instance down first.
 *
 * TIME-ON-PAGE CONVENTION (shared with AnalyticsEventController): every
 * `time_on_page` event carries the DELTA of visible seconds since the previous
 * send for this page life — never a cumulative total. The server SUMs deltas
 * into analytics_sessions.duration_seconds, so any number of flushes
 * (tab hidden, tab shown again, pagehide, SPA unmount) add up to the true
 * visible time exactly once. Hidden time is never counted.
 */
export function initAnalytics(): () => void {
    if (activeTeardown) {
        activeTeardown();
    }

    // Skip CMS/admin pages. Derived from config/nav.php at build time.
    if (isCmsPath(window.location.pathname)) {
        return noop;
    }

    // Skip internal traffic — CMS users previewing the public site would
    // otherwise inflate bounce rate / session counts. Server-side guard in
    // AnalyticsEventController is the authoritative check; this short-circuits
    // the network round-trip.
    const userRoles = getInertiaPageProps()?.userRoles;
    if (Array.isArray(userRoles) && userRoles.length > 0) {
        return noop;
    }

    const ctx: PageContext = {
        page_path: window.location.pathname,
        page_title: document.title,
        article_id: getArticleId(),
    };

    // Capture click IDs on first pageview
    captureClickIds();

    // Page view
    send({ event_type: 'pageview' }, ctx);

    // Scroll tracking
    let maxScroll = 0;
    const milestones = new Set<number>();

    const handleScroll = () => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;
        const pct = Math.round((scrollTop / docHeight) * 100);
        if (pct > maxScroll) maxScroll = pct;

        for (const milestone of [25, 50, 75, 100]) {
            if (pct >= milestone && !milestones.has(milestone)) {
                milestones.add(milestone);
                send({ event_type: 'scroll', scroll_depth: milestone }, ctx);
            }
        }
    };

    // Time on page — visible milliseconds not yet reported.
    let visibleSince: number | null = document.visibilityState === 'hidden' ? null : Date.now();
    let unreportedMs = 0;
    let exited = false;

    const flushTime = () => {
        if (visibleSince !== null) {
            unreportedMs += Date.now() - visibleSince;
            visibleSince = null;
        }
        const seconds = Math.floor(unreportedMs / 1000);
        if (seconds >= 1) {
            // Keep the sub-second remainder so successive deltas stay exact.
            unreportedMs -= seconds * 1000;
            send({ event_type: 'time_on_page', duration_seconds: seconds, scroll_depth: maxScroll }, ctx);
        }
    };

    // Tab hidden → flush the delta; tab shown again → restart the clock.
    const handleVisibility = () => {
        if (document.visibilityState === 'hidden') {
            flushTime();
        } else if (visibleSince === null && !exited) {
            visibleSince = Date.now();
        }
    };

    // pagehide is the reliable end-of-life signal (fires on mobile Safari and
    // in-app browsers where beforeunload does not). visibilitychange(hidden)
    // usually precedes it, so the time delta here is normally zero.
    const handlePageHide = () => {
        if (exited) return;
        exited = true;
        flushTime();
        send({ event_type: 'exit', scroll_depth: maxScroll }, ctx);
    };

    // bfcache restore: the page is alive again, resume counting.
    const handlePageShow = (e: PageTransitionEvent) => {
        if (e.persisted) {
            exited = false;
            visibleSince = Date.now();
        }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    let torn = false;
    const teardown = () => {
        if (torn) return;
        torn = true;
        if (activeTeardown === teardown) activeTeardown = null;
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('pageshow', handlePageShow);
        // SPA navigation away from this page: report the remaining visible
        // time for THIS page (ctx pins the old path). Not an exit — the next
        // page's pageview follows immediately.
        if (!exited) flushTime();
    };
    activeTeardown = teardown;

    return teardown;
}

/**
 * Emit a `form_submitted` analytics event after a successful form submission.
 *
 * Fire-and-forget: the call resolves whenever the network does, but callers
 * must NOT await it — the event is observational, never on the critical path
 * for the user-visible success UX. We swallow all errors (network, throttle,
 * 422) silently because no end-user action should fail because the analytics
 * pipeline hiccupped.
 *
 * Backed by `apiFetch` so we get CSRF + Sentry + the rest of the standard
 * client behaviour rather than a raw `fetch`.
 */
export function trackFormSubmitted(formId: string, eventData: Record<string, string | number | boolean | null | undefined> = {}): void {
    // (1) GTM dataLayer push — unified contract every form share. One GTM
    // trigger on `form_submitted` catches them all; `form_type` discriminates.
    // form_location captures the page path so funnel/page-level conversion
    // analysis in GA4 is straightforward.
    if (typeof window !== 'undefined') {
        const dl = (window as unknown as { dataLayer?: unknown[] }).dataLayer ?? ((window as unknown as { dataLayer?: unknown[] }).dataLayer = []);
        const dataLayerEvent: Record<string, unknown> = {
            event: 'form_submitted',
            form_type: formId,
            form_location: window.location.pathname,
        };
        for (const [k, v] of Object.entries(eventData)) {
            if (v === null || v === undefined) continue;
            dataLayerEvent[k] = v;
        }
        dl.push(dataLayerEvent);
    }

    // (2) Internal analytics — populates analytics_events so the unified
    // analytics system can count conversions independently of GA4 reliability.
    // Match the existing `send()` payload shape. session_id is lazily created
    // by getSessionId() so this still works on pages where initAnalytics() was
    // skipped (e.g. CMS-adjacent surfaces with public forms embedded).
    const utm = getUTMParams();
    const experiments = getExperimentAssignments();

    // Stringify scalars + drop nullish — server validates each value as scalar
    // ≤ 200 chars, and Laravel's array max:8 only counts present keys.
    const cleanedEventData: Record<string, string> = { form_id: formId };
    for (const [k, v] of Object.entries(eventData)) {
        if (v === null || v === undefined) continue;
        cleanedEventData[k] = String(v).slice(0, 200);
    }

    const body = JSON.stringify({
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        click_ids: captureClickIds(),
        page_path: window.location.pathname,
        page_title: document.title,
        article_id: getArticleId(),
        referrer: document.referrer || null,
        ...utm,
        ...(experiments ? { experiments } : {}),
        event_type: 'form_submitted',
        event_data: cleanedEventData,
    });

    // Fire-and-forget. apiFetch with silent:true suppresses error toasts;
    // the .catch swallows the rejection so the form's success path isn't
    // affected by analytics-pipeline failures.
    apiFetch(API_URL, {
        method: 'POST',
        body,
        silent: true,
    }).catch(() => {
        // Intentionally empty: analytics is best-effort, never blocking.
    });
}
